import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface RateCard {
  id: number;
  carrier_id: number;
  carrier_name: string | null;
  carrier_code: string | null;
  name: string;
  description: string | null;
  service_type: string | null;
  is_active: boolean;
  zone_names: string[] | null;
  zone_count: number;
  base_weight_kg: number;
  additional_weight_kg: number;
  fuel_surcharge_percent: number;
  min_freight: number;
  volumetric_divisor: number;
  currency: string;
  slabs_count: number;
  surcharges_count: number;
  created_at: string | null;
  slabs?: Slab[];
  surcharges?: Surcharge[];
}

interface Slab {
  id: number;
  weight_from_kg: number;
  weight_to_kg: number;
  is_additional: boolean;
  zone_rates: Record<string, number>;
}

interface Surcharge {
  id: number;
  name: string;
  charge_type: string;
  charge_value: number;
  min_charge: number;
  max_charge: number;
  applies_to: string;
  description: string | null;
}

interface RateQuote {
  carrier_code: string;
  carrier_name: string;
  rate_card_name: string;
  service_type: string;
  zone: string;
  chargeable_weight_kg: number;
  volumetric_weight_kg: number;
  base_freight: number;
  fuel_surcharge: number;
  cod_charge: number;
  insurance_charge: number;
  handling_charge: number;
  other_charges: number;
  subtotal: number;
  gst: number;
  total: number;
  currency: string;
  estimated_days_min: number;
  estimated_days_max: number;
}

interface Carrier { id: number; code: string; name: string; }

interface RateCardManagerProps { userRole?: string; }

const RateCardManager: React.FC<RateCardManagerProps> = ({ userRole = 'viewer' }) => {
  const [cards, setCards] = useState<RateCard[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState<RateCard | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'cards' | 'calculator'>('cards');

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    carrier_id: '', name: '', service_type: 'express', description: '',
    base_weight_kg: '0.5', additional_weight_kg: '0.5', fuel_surcharge_percent: '0',
    zone_names: 'A,B,C,D', volumetric_divisor: '5000',
  });

  // Calculator state
  const [calcForm, setCalcForm] = useState({
    origin_pincode: '560068', destination_pincode: '', weight_kg: '0.5',
    length_cm: '', width_cm: '', height_cm: '',
    payment_mode: 'prepaid', declared_value: '0', cod_amount: '0',
    carrier_id: '',
  });
  const [calcResults, setCalcResults] = useState<RateQuote[]>([]);
  const [calculating, setCalculating] = useState(false);

  // Upload states
  const [uploadingSlabs, setUploadingSlabs] = useState(false);
  const [uploadingZones, setUploadingZones] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  // Surcharge form
  const [showSurchargeForm, setShowSurchargeForm] = useState(false);
  const [surchargeForm, setSurchargeForm] = useState({
    name: 'cod', charge_type: 'min_or_percent', charge_value: '3',
    min_charge: '60', max_charge: '0', applies_to: 'cod', description: '',
  });

  const isAdmin = userRole === 'admin';

  useEffect(() => { fetchCards(); fetchCarriers(); }, []);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const res = await api.get('/shipping/rates/cards');
      setCards(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchCarriers = async () => {
    try {
      const res = await api.get('/shipping/carriers/');
      const data = Array.isArray(res.data) ? res.data : [];
      setCarriers(data.map((c: any) => ({ id: c.id, code: c.code, name: c.name })));
    } catch (e) { console.error(e); }
  };

  const fetchCardDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/shipping/rates/cards/${id}`);
      setSelectedCard(res.data);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const handleCreateCard = async () => {
    try {
      const zones = createForm.zone_names.split(',').map(z => z.trim()).filter(Boolean);
      await api.post('/shipping/rates/cards', {
        carrier_id: Number(createForm.carrier_id),
        name: createForm.name,
        service_type: createForm.service_type,
        description: createForm.description,
        base_weight_kg: Number(createForm.base_weight_kg),
        additional_weight_kg: Number(createForm.additional_weight_kg),
        fuel_surcharge_percent: Number(createForm.fuel_surcharge_percent),
        volumetric_divisor: Number(createForm.volumetric_divisor),
        zone_names: zones,
      });
      setShowCreateForm(false);
      fetchCards();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCard = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/shipping/rates/cards/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      if (selectedCard?.id === deleteConfirm.id) setSelectedCard(null);
      fetchCards();
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleUploadSlabs = async (cardId: number, file: File) => {
    setUploadingSlabs(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/shipping/rates/cards/${cardId}/upload-slabs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(res.data.message || 'Slabs uploaded');
      fetchCardDetail(cardId);
    } catch (e: any) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingSlabs(false); }
  };

  const handleUploadZones = async (carrierId: number, file: File) => {
    setUploadingZones(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/shipping/rates/zones/upload?carrier_id=${carrierId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert(res.data.message || 'Zone mappings uploaded');
    } catch (e: any) { alert(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingZones(false); }
  };

  const handleAddSurcharge = async () => {
    if (!selectedCard) return;
    try {
      await api.post(`/shipping/rates/cards/${selectedCard.id}/surcharges`, {
        ...surchargeForm,
        charge_value: Number(surchargeForm.charge_value),
        min_charge: Number(surchargeForm.min_charge),
        max_charge: Number(surchargeForm.max_charge),
      });
      setShowSurchargeForm(false);
      fetchCardDetail(selectedCard.id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteSurcharge = async (surchargeId: number) => {
    if (!selectedCard) return;
    try {
      await api.delete(`/shipping/rates/cards/${selectedCard.id}/surcharges/${surchargeId}`);
      fetchCardDetail(selectedCard.id);
    } catch (e: any) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleCalculate = async () => {
    setCalculating(true);
    setCalcResults([]);
    try {
      const res = await api.post('/shipping/rates/calculate', {
        origin_pincode: calcForm.origin_pincode,
        destination_pincode: calcForm.destination_pincode,
        weight_kg: Number(calcForm.weight_kg),
        length_cm: Number(calcForm.length_cm) || 0,
        width_cm: Number(calcForm.width_cm) || 0,
        height_cm: Number(calcForm.height_cm) || 0,
        payment_mode: calcForm.payment_mode,
        declared_value: Number(calcForm.declared_value) || 0,
        cod_amount: Number(calcForm.cod_amount) || 0,
        carrier_id: calcForm.carrier_id ? Number(calcForm.carrier_id) : null,
      });
      setCalcResults(res.data.quotes || []);
      if (res.data.quotes?.length === 0) alert('No rates found. Check zone mappings and rate card config.');
    } catch (e: any) { alert(e.response?.data?.detail || 'Calculation failed'); }
    finally { setCalculating(false); }
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tab Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b', marginRight: '16px' }}>Rate Cards</h2>
        <button onClick={() => setActiveTab('cards')} style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: activeTab === 'cards' ? 'none' : '1px solid #e2e8f0', background: activeTab === 'cards' ? '#0d9488' : 'white', color: activeTab === 'cards' ? 'white' : '#334155', cursor: 'pointer' }}>
          Rate Cards
        </button>
        <button onClick={() => setActiveTab('calculator')} style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: activeTab === 'calculator' ? 'none' : '1px solid #e2e8f0', background: activeTab === 'calculator' ? '#0d9488' : 'white', color: activeTab === 'calculator' ? 'white' : '#334155', cursor: 'pointer' }}>
          Rate Calculator
        </button>
        {isAdmin && activeTab === 'cards' && (
          <button onClick={() => setShowCreateForm(true)} style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>
            + New Rate Card
          </button>
        )}
      </div>

      {/* Rate Calculator Tab */}
      {activeTab === 'calculator' && (
        <div style={{ padding: '24px', overflow: 'auto' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', maxWidth: '900px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: '#1e293b' }}>Rate Calculator</h3>
            <div className="form-grid-4" style={{ gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Origin Pincode</label>
                <input value={calcForm.origin_pincode} onChange={e => setCalcForm({...calcForm, origin_pincode: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Destination Pincode *</label>
                <input value={calcForm.destination_pincode} onChange={e => setCalcForm({...calcForm, destination_pincode: e.target.value})} placeholder="400001" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Weight (kg) *</label>
                <input type="number" step="0.1" value={calcForm.weight_kg} onChange={e => setCalcForm({...calcForm, weight_kg: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Payment Mode</label>
                <select value={calcForm.payment_mode} onChange={e => setCalcForm({...calcForm, payment_mode: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Length (cm)</label>
                <input type="number" value={calcForm.length_cm} onChange={e => setCalcForm({...calcForm, length_cm: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Width (cm)</label>
                <input type="number" value={calcForm.width_cm} onChange={e => setCalcForm({...calcForm, width_cm: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Height (cm)</label>
                <input type="number" value={calcForm.height_cm} onChange={e => setCalcForm({...calcForm, height_cm: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Declared Value (₹)</label>
                <input type="number" value={calcForm.declared_value} onChange={e => setCalcForm({...calcForm, declared_value: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
              </div>
              {calcForm.payment_mode === 'cod' && (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>COD Amount (₹)</label>
                  <input type="number" value={calcForm.cod_amount} onChange={e => setCalcForm({...calcForm, cod_amount: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
              )}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Carrier (optional)</label>
                <select value={calcForm.carrier_id} onChange={e => setCalcForm({...calcForm, carrier_id: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="">All Carriers</option>
                  {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <button onClick={handleCalculate} disabled={calculating || !calcForm.destination_pincode} style={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer', opacity: calculating ? 0.6 : 1 }}>
              {calculating ? 'Calculating...' : 'Calculate Rates'}
            </button>

            {/* Results */}
            {calcResults.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '12px', color: '#1e293b' }}>
                  {calcResults.length} Quote{calcResults.length !== 1 ? 's' : ''} Found
                </h4>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '11px', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Carrier</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Service</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>Zone</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Weight</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Base</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Fuel</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>COD</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>GST</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>Total</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center' }}>ETA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calcResults.map((q, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx === 0 ? '#f0fdfa' : 'white' }}>
                        <td style={{ padding: '10px 10px', fontWeight: 600 }}>{q.carrier_name}</td>
                        <td style={{ padding: '10px 10px' }}>{q.service_type}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'center' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: '#e0f2fe', color: '#0c4a6e' }}>{q.zone}</span>
                        </td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{q.chargeable_weight_kg} kg</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right' }}>₹{q.base_freight}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right' }}>₹{q.fuel_surcharge}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right' }}>{q.cod_charge > 0 ? `₹${q.cod_charge}` : '—'}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right' }}>₹{q.gst}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 700, fontSize: '13px', color: '#0d9488' }}>₹{q.total}</td>
                        <td style={{ padding: '10px 10px', textAlign: 'center', fontSize: '11px', color: '#64748b' }}>
                          {q.estimated_days_min && q.estimated_days_max ? `${q.estimated_days_min}-${q.estimated_days_max}d` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rate Cards Tab */}
      {activeTab === 'cards' && (
        <div className="split-master-detail">
          {/* Card List */}
          <div className="split-list">
            {cards.length === 0 && !showCreateForm && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                No rate cards. Click "+ New Rate Card" to create one.
              </div>
            )}
            {cards.map(c => (
              <div key={c.id} onClick={() => fetchCardDetail(c.id)} style={{
                padding: '12px 14px', marginBottom: '6px', borderRadius: '8px', cursor: 'pointer',
                background: selectedCard?.id === c.id ? '#f0fdfa' : 'white',
                border: selectedCard?.id === c.id ? '1px solid #0d9488' : '1px solid #e2e8f0',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                  <span style={{ padding: '2px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600, background: c.is_active ? '#d4edda' : '#f8d7da', color: c.is_active ? '#155724' : '#721c24' }}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  {c.carrier_name} • {c.service_type || 'General'} • {c.slabs_count} slabs
                </div>
              </div>
            ))}
          </div>

          {/* Detail Panel */}
          <div className="split-detail">
            {/* Create Form */}
            {showCreateForm && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', maxWidth: '600px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>New Rate Card</h3>
                <div className="form-grid-2" style={{ marginBottom: 0 }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Carrier *</label>
                    <select value={createForm.carrier_id} onChange={e => setCreateForm({...createForm, carrier_id: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                      <option value="">Select Carrier</option>
                      {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Name *</label>
                    <input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} placeholder="BlueDart Air Etail 2025" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Service Type</label>
                    <select value={createForm.service_type} onChange={e => setCreateForm({...createForm, service_type: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                      <option value="express">Express (Air)</option>
                      <option value="surface">Surface</option>
                      <option value="economy">Economy</option>
                      <option value="standard">Standard</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Fuel Surcharge %</label>
                    <input type="number" value={createForm.fuel_surcharge_percent} onChange={e => setCreateForm({...createForm, fuel_surcharge_percent: e.target.value})} placeholder="61" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Base Weight (kg)</label>
                    <input type="number" step="0.1" value={createForm.base_weight_kg} onChange={e => setCreateForm({...createForm, base_weight_kg: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Additional Weight (kg)</label>
                    <input type="number" step="0.1" value={createForm.additional_weight_kg} onChange={e => setCreateForm({...createForm, additional_weight_kg: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Zones (comma-separated)</label>
                    <input value={createForm.zone_names} onChange={e => setCreateForm({...createForm, zone_names: e.target.value})} placeholder="A,B,C,D" style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button onClick={handleCreateCard} disabled={!createForm.carrier_id || !createForm.name} style={{ padding: '6px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>Create</button>
                  <button className="btn btn-cancel" onClick={() => setShowCreateForm(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Card Detail */}
            {selectedCard && !showCreateForm && (
              <div>
                {/* Header */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{selectedCard.name}</h3>
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                        {selectedCard.carrier_name} • {selectedCard.service_type} • Zones: {selectedCard.zone_names?.join(', ')} • Fuel: {selectedCard.fuel_surcharge_percent}%
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {isAdmin && (
                        <button onClick={() => setDeleteConfirm({show: true, id: selectedCard.id, name: selectedCard.name})}
                          style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #fca5a5', background: '#fee2e2', color: '#991b1b', cursor: 'pointer' }}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Config summary */}
                  <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '11px', color: '#475569' }}>
                    <span>Base: {selectedCard.base_weight_kg}kg</span>
                    <span>Additional: {selectedCard.additional_weight_kg}kg</span>
                    <span>Vol Divisor: {selectedCard.volumetric_divisor}</span>
                    <span>Min Freight: ₹{selectedCard.min_freight}</span>
                  </div>
                </div>

                {/* Upload Section */}
                {isAdmin && (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px 24px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Upload Rate Slabs (CSV)</label>
                      <input type="file" accept=".csv" disabled={uploadingSlabs} onChange={e => { if (e.target.files?.[0]) handleUploadSlabs(selectedCard.id, e.target.files[0]); }} style={{ fontSize: '12px' }} />
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>Columns: weight_from, weight_to, is_additional, zone_A, zone_B, ...</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '4px', color: '#334155' }}>Upload Zone Mappings (CSV)</label>
                      <input type="file" accept=".csv" disabled={uploadingZones} onChange={e => { if (e.target.files?.[0]) handleUploadZones(selectedCard.carrier_id, e.target.files[0]); }} style={{ fontSize: '12px' }} />
                      <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>Columns: pincode, zone (optional: city, state)</div>
                    </div>
                  </div>
                )}

                {/* Slabs Table */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>
                    Rate Slabs ({selectedCard.slabs?.length || 0})
                  </h4>
                  {selectedCard.slabs && selectedCard.slabs.length > 0 ? (
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '10px', fontWeight: 600, color: '#64748b', letterSpacing: '0.5px' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Weight Range</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center' }}>Type</th>
                          {selectedCard.zone_names?.map(z => (
                            <th key={z} style={{ padding: '6px 10px', textAlign: 'right' }}>Zone {z}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCard.slabs.map(slab => (
                          <tr key={slab.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>
                              {slab.weight_from_kg} – {slab.weight_to_kg >= 9999 ? '∞' : slab.weight_to_kg} kg
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                              <span style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '10px', background: slab.is_additional ? '#fff3cd' : '#cce5ff', color: slab.is_additional ? '#856404' : '#004085' }}>
                                {slab.is_additional ? 'Additional' : 'Base'}
                              </span>
                            </td>
                            {selectedCard.zone_names?.map(z => (
                              <td key={z} style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                                ₹{slab.zone_rates[z] ?? '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>No slabs configured. Upload a CSV to add rate slabs.</p>
                  )}
                </div>

                {/* Surcharges */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px 24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Surcharges ({selectedCard.surcharges?.length || 0})</h4>
                    {isAdmin && <button onClick={() => setShowSurchargeForm(true)} style={{ padding: '3px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>+ Add</button>}
                  </div>
                  {selectedCard.surcharges && selectedCard.surcharges.length > 0 ? (
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f8fafb', textTransform: 'uppercase', fontSize: '10px', fontWeight: 600, color: '#64748b' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Name</th>
                          <th style={{ padding: '6px 10px', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' }}>Value</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' }}>Min</th>
                          <th style={{ padding: '6px 10px', textAlign: 'center' }}>Applies To</th>
                          <th style={{ padding: '6px 10px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCard.surcharges.map(s => (
                          <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 10px', fontWeight: 600, textTransform: 'capitalize' }}>{s.name.replace(/_/g, ' ')}</td>
                            <td style={{ padding: '6px 10px' }}>{s.charge_type}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                              {s.charge_type === 'percent' || s.charge_type === 'min_or_percent' ? `${s.charge_value}%` : `₹${s.charge_value}`}
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{s.min_charge > 0 ? `₹${s.min_charge}` : '—'}</td>
                            <td style={{ padding: '6px 10px', textAlign: 'center' }}>{s.applies_to}</td>
                            <td style={{ padding: '6px 10px' }}>
                              {isAdmin && <button onClick={() => handleDeleteSurcharge(s.id)} style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>No surcharges configured.</p>
                  )}

                  {/* Add Surcharge Form */}
                  {showSurchargeForm && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <div className="form-grid-4">
                        <div>
                          <label style={{ fontSize: '10px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Name</label>
                          <select value={surchargeForm.name} onChange={e => setSurchargeForm({...surchargeForm, name: e.target.value})} style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                            <option value="cod">COD</option>
                            <option value="insurance">Insurance / FOV</option>
                            <option value="handling">Handling</option>
                            <option value="awb">AWB Charge</option>
                            <option value="rto">RTO</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Type</label>
                          <select value={surchargeForm.charge_type} onChange={e => setSurchargeForm({...surchargeForm, charge_type: e.target.value})} style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                            <option value="flat">Flat (₹)</option>
                            <option value="percent">Percent (%)</option>
                            <option value="min_or_percent">Higher of Min or %</option>
                            <option value="per_kg">Per kg</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Value</label>
                          <input type="number" value={surchargeForm.charge_value} onChange={e => setSurchargeForm({...surchargeForm, charge_value: e.target.value})} style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', fontWeight: 600, display: 'block', marginBottom: '2px' }}>Min Charge (₹)</label>
                          <input type="number" value={surchargeForm.min_charge} onChange={e => setSurchargeForm({...surchargeForm, min_charge: e.target.value})} style={{ width: '100%', padding: '5px 8px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px', boxSizing: 'border-box' }} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                        <select value={surchargeForm.applies_to} onChange={e => setSurchargeForm({...surchargeForm, applies_to: e.target.value})} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '4px' }}>
                          <option value="all">Applies to: All</option>
                          <option value="cod">COD only</option>
                          <option value="prepaid">Prepaid only</option>
                        </select>
                        <button onClick={handleAddSurcharge} style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', border: 'none', background: '#0d9488', color: 'white', cursor: 'pointer' }}>Add</button>
                        <button className="btn btn-cancel btn-sm" onClick={() => setShowSurchargeForm(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!selectedCard && !showCreateForm && (
              <div style={{ textAlign: 'center', color: '#94a3b8', paddingTop: '80px' }}>
                <p style={{ fontSize: '14px' }}>Select a rate card to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Rate Card"
        message="Delete this rate card and all its slabs and surcharges?"
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteCard}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default RateCardManager;
